import { format } from 'date-fns'
import { GraphQLError } from 'graphql'
import { omit } from 'lodash'

import {
  createMortgage,
  createMortgageHelper,
  createMortgageWithProductAndLinkToCase,
  fetchMortgageByVersion,
  getProductListByProductCodeAndLender,
  sourceProductBySourcingProductId,
  updateMortgageAndProduct,
  updateMortgageHelper,
  updateMortgages,
} from '../api/mortgage'
import { addMortgageToCase } from '../api/mortgage_case'
import { addMortgageToProduct } from '../api/mortgage_product'
import { MortgageProductLoader, sourceMortgages } from '../api/product'
import { fetchProperty } from '../api/property'
import { SummaryLoader } from '../api/summary'
import { associateMortgageWithProductCP, createMortgageCP, updateMortgageCP } from '../api_client_portal/mortgage'
import { createMortgageProductCP, MortgageProductLoaderCp } from '../api_client_portal/product'
import type { Resolvers } from '../generated/server/resolvers'
import { mortgageLoader } from '../loaders/mortgage'
import {
  formatGetAcreMortgageProductResponseParentAsMortgageProductResponse,
  formatSourcedMortgageProductResponseList,
} from '../utils/schemaMapping/mortgageProduct'
import deleteMortgageHelper from './helpers/deleteMortgageHelper'
import { getEndDate, getPropertyValue, getProposedStartDate } from './util'

const resolvers: Resolvers = {
  Query: {
    mortgageVersion: async (_parent, { id, version, show_history }) =>
      await fetchMortgageByVersion({ mortgage_id: id, version, show_history }),
    getMortgageProductCodes: async (_parent, { isBtl, lender, productCode }) =>
      await getProductListByProductCodeAndLender(isBtl, productCode?.toUpperCase(), lender),
    getMortgageProductBySourcingProductId: async (_parent, { input }) => {
      const { isBtl, sourcingProductId, loanAmount, valuationAmount, isCurrent } = input
      return await sourceProductBySourcingProductId(isBtl, sourcingProductId, loanAmount, valuationAmount, isCurrent)
    },
  },
  Mutation: {
    sourceMortgages: async (_parent, { input }) => {
      const response = await sourceMortgages(input)
      const { products } = response
      return {
        ...response,
        products: products ? formatSourcedMortgageProductResponseList(products) : null,
      }
    },

    addMortgageAndProductCp: async (_parent, { mortgageInput, mortgageProductInput }) => {
      const { status, term, term_unit, mortgage_start_date } = mortgageInput
      const defaultStartDate =
        mortgageInput.status === 'STATUS_CURRENT'
          ? format(new Date(), 'yyyy-MM-dd')
          : getProposedStartDate(status || 'INVALID_STATUS')
      const startDate = mortgage_start_date || defaultStartDate
      const prepopulatedMortgageInput = {
        ...mortgageInput,
        interest_only_amount: mortgageInput.interest_only_amount || '0',
        mortgage_start_date: startDate,
        mortgage_end_date: getEndDate(startDate, term, term_unit),
      }
      const newMortgage = await createMortgageCP(prepopulatedMortgageInput)

      if (!newMortgage) {
        return null
      }

      const newProduct = await createMortgageProductCP(mortgageProductInput)

      await associateMortgageWithProductCP({
        product_id: newProduct.product_id,
        mortgage_id: newMortgage.id,
      })

      if (newProduct.product_id) {
        const product = await MortgageProductLoaderCp.load(newProduct.product_id)

        if (product) {
          const mortgage = {
            ...newMortgage,
            id: newMortgage.mortgage_id,
            mortgage_product_id: newProduct.product_id,
          }

          try {
            return { ...mortgage, mortgage_product: product }
          } catch (e) {
            console.error(e)
            return mortgage
          }
        }
      }

      return null
    },

    saveMortgageProductToCase: async (_parent, { input }) => {
      const {
        status,
        clientIds,
        case_id,
        repayment_amount,
        product_code,
        source_response,
        mortgage_amount,
        term,
        term_unit,
        initial_monthly_payment,
        property_secured_ids,
        preference_target_property_value,
        part_investment_loan_required_investment_part,
      } = input

      let property: PropertyVersion | GraphQLError | null = null

      if (property_secured_ids && property_secured_ids[0]) {
        property = await fetchProperty(property_secured_ids[0])

        if (property instanceof GraphQLError) {
          throw property
        }
      }

      const startDate =
        status === 'STATUS_CURRENT'
          ? format(new Date(), 'yyyy-MM-dd')
          : getProposedStartDate(status || 'INVALID_STATUS')

      const newMortgage = await createMortgage({
        client_ids: clientIds,
        term: term,
        term_unit,
        status,
        mortgage_amount,
        repayment_amount,
        property_secured_ids,
        monthly_payment: initial_monthly_payment ? initial_monthly_payment.toString() : null,
        interest_only_amount: part_investment_loan_required_investment_part
          ? part_investment_loan_required_investment_part
          : '0',
        property_value: getPropertyValue(property, status || 'INVALID_STATUS', preference_target_property_value || ''),
        mortgage_start_date: startDate,
        mortgage_end_date: getEndDate(startDate, term, term_unit),
      })

      if (!newMortgage) return

      const { product_id } = await addMortgageToProduct({
        product_code,
        source_response,
        mortgage_id: newMortgage.id,
      })

      if (product_id) {
        await addMortgageToCase({
          case_id,
          mortgage_id: newMortgage.mortgage_id,
        })
        const product = await MortgageProductLoader.load(product_id)

        if (product) {
          const mortgage = {
            ...newMortgage,
            id: newMortgage.mortgage_id,
            mortgage_product_id: product_id,
          }

          // We use a try catch in case the 'source_response' is improperly formatted
          // If that is the case, we'll just return the mortgage without it
          try {
            const formattedSourceResponse = source_response ? JSON.parse(source_response) : null
            return { ...mortgage, source_response: formattedSourceResponse }
          } catch (e) {
            console.error(e)
            return mortgage
          }
        }
      }

      // Clear summary loader as it will contain stale data
      SummaryLoader.clearAll()
    },
    addMortgageToCase: async (_parent, { id, mortgageId }) => {
      SummaryLoader.clearAll()

      try {
        return await addMortgageToCase({ case_id: id, mortgage_id: mortgageId })
      } catch (e) {
        console.error(e)
      }
    },
    createMortgage: async (_parent, { caseId, mortgageInput, mortgageProductInput }) => {
      if (caseId) mortgageLoader.clear({ filter_case_id: caseId })
      const input = { caseId, mortgageInput, mortgageProductInput }
      const mortgageFormatted = await createMortgageHelper(input)
      return mortgageFormatted
    },
    updateMortgage: async (_parent, { id, input }) => {
      SummaryLoader.clearAll()

      const mortgage = await updateMortgageHelper(id, input)
      if (!mortgage) return

      return {
        ...mortgage,
        id: String(mortgage.id),
      } as Mortgage
    },
    updateMortgageCp: async (_parent, { id, input }) => {
      const mortgage = await updateMortgageCP(id, input)
      return mortgage
    },
    updateMortgages: async (_parent, { input }) => {
      if (!input?.length) {
        throw new GraphQLError('No mortgages provided for update', {
          extensions: {
            status: 400,
          },
        })
      }
      const mortgages = await updateMortgages(input)

      mortgageLoader.clearAll()
      SummaryLoader.clearAll()

      return mortgages || []
    },
    deleteMortgage: async (_parent, { mortgageId, caseId, isPropertyPortfolio }) => {
      SummaryLoader.clearAll()
      return await deleteMortgageHelper(mortgageId, caseId, isPropertyPortfolio)
    },
    deleteMortgages: async (_parent, { mortgageIds, caseId, isPropertyPortfolio }) => {
      SummaryLoader.clearAll()
      return await Promise.all(
        mortgageIds.map(async (id) => await deleteMortgageHelper(id, caseId, isPropertyPortfolio)),
      )
    },
    createMortgageWithProductAndLinkToCase: async (_, { input }) => {
      return await createMortgageWithProductAndLinkToCase(input)
    },
    updateMortgageAndProduct: async (_, { input }) => {
      if (!input?.mortgage_id) {
        throw new GraphQLError('Mortgage ID is required for update', {
          extensions: {
            status: 400,
          },
        })
      }
      mortgageLoader.clearAll()
      SummaryLoader.clearAll()
      return await updateMortgageAndProduct(input.mortgage_id, omit(input, 'mortgage_id'))
    },
  },
  Mortgage: {
    mortgage_product: async ({ mortgage_product_id, mortgage_product }) => {
      // If the mortgage product is already loaded in the parent, return it directly
      // This is resolved using the summary endpoint to improve performance where applicable
      if (mortgage_product?.id) return mortgage_product

      if (!mortgage_product_id) return null

      const response = await MortgageProductLoader.load(mortgage_product_id)
      if (!response) return null
      return formatGetAcreMortgageProductResponseParentAsMortgageProductResponse(response)
    },
    mortgage_productCp: async (parent: Mortgage) => {
      const { mortgage_product_id } = parent
      if (!mortgage_product_id) return null

      const response = await MortgageProductLoaderCp.load(mortgage_product_id)
      if (!response) return null
      return formatGetAcreMortgageProductResponseParentAsMortgageProductResponse(response)
    },
  },
}

export default resolvers
